import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { EventqToastService, type AppEvent, type PageSyncState } from '@ecam/eventq-components';
import {
  buildAgentTabCommands,
  parseAgentTabRoute,
  TAB_EVENTS_PAGE,
  tabProcessingRouteSegments,
} from '../routing/browser-tab-routes';
import { BrowserTabService } from './browser-tab.service';
import { AgentEventsService } from './agent-events.service';
import { AgentPreferencesService } from './agent-preferences.service';
import { EventHandleService } from './event-handle.service';
import { EventProcessingMode } from '../models/browser-tab.models';
import { AuthSessionService } from './auth-session.service';

interface TabNavigationOptions {
  syncState?: boolean;
}
interface SyncOptions {
  hardSync?: boolean;
  /** Apply empty hard-sync during boot (ownership rebuild). Peer mid-reload stays ignored. */
  applyEmptyDuringBoot?: boolean;
}

@Injectable({ providedIn: 'root' })
export class BrowserTabNavigationService {
  private readonly router = inject(Router);
  private readonly browserTabs = inject(BrowserTabService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly agentEvents = inject(AgentEventsService);
  private readonly eventHandles = inject(EventHandleService);
  private readonly preferences = inject(AgentPreferencesService);
  private readonly auth = inject(AuthSessionService);
  private readonly toast = inject(EventqToastService);

  private workspaceBootstrapping = false;

  /**
   * Writes the route event onto its tab.
   * After boot, publishes that tab so Resolve can reopen it. During boot the
   * page-state init broadcast is the one that includes the refreshed event.
   */
  publishRouteEvent(tabId: string, eventId: string, mode: EventProcessingMode | null): void {
    if (!tabId || !eventId || !this.browserTabs.hasTab(tabId)) {
      return;
    }

    const current = this.browserTabs.getPageState(tabId);
    const changed = current.activeEventId !== eventId || (!!mode && current.eventProcessingMode !== mode);
    this.browserTabs.updatePageState(tabId, {
      activeEventId: eventId,
      ...(mode ? { eventProcessingMode: mode } : {}),
    });
    if (changed && !this.workspaceBootstrapping) {
      this.agentEvents.broadcastStateSync();
    }
  }

  /** Ignore empty hard-sync while boot is in progress (peer mid-reload). */
  beginWorkspaceBoot(): void {
    this.workspaceBootstrapping = true;
  }

  completeWorkspaceBoot(): void {
    this.workspaceBootstrapping = false;
  }

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        this.syncFromUrl(event.urlAfterRedirects);
      });
    this.agentEvents.events$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.type === 'STATE_SYNC' && event.pageState) {
          const hardSync = !this.auth.shouldManageEvents();
          this.syncFromPageState(event.pageState, { hardSync });
          return;
        }
        if (event.type === 'EVENT_CLOSED' || event.type === 'EVENT_PARKED') {
          void this.applyPeerEventLifecycle(event);
        }
      });
  }

  private async applyPeerEventLifecycle(event: AppEvent): Promise<void> {
    const eventId = event.id;
    if (!eventId) {
      return;
    }

    const label = event.label ?? eventId;
    if (event.type === 'EVENT_CLOSED') {
      this.toast.eventClosed(label);
    } else {
      this.toast.eventParked(label, event.durationLabel ?? '');
    }

    const tab = this.browserTabs.findTab(eventId);
    if (!tab) {
      return;
    }

    if (this.browserTabs.tabs().length > 1) {
      await this.closeTabWorkspace(tab.id, { syncState: false });
      return;
    }

    await this.eventHandles.releaseHandleForTabState(this.browserTabs.getPageState(tab.id).eventHandleId);
    this.browserTabs.selectTab(tab.id);
    this.browserTabs.updateActivePageState({
      activeEventId: null,
      eventHandleId: null,
      claimedAt: null,
      pendingAction: null,
    });
    this.browserTabs.updateTabLabel(tab.id, 'New Event');
    this.browserTabs.updateTabRoutePath(tab.id, TAB_EVENTS_PAGE);

    // Same rule as a close or park started in EventQ: the only open workspace
    // claims the next queue event when Auto-Process is on. Other open tabs
    // stay as they are. Resolve hears the claim through the state sync that
    // opening the event publishes.
    if (this.preferences.autoProcess()) {
      const claimed = await this.eventHandles.claimNextForTab(tab.id, {
        excludeEventIds: [eventId],
      });
      if (claimed?.ok) {
        this.browserTabs.selectTab(tab.id);
        await this.openClaimedProcessEvent(claimed.handle.eventId);
        return;
      }
    }

    await this.navigateWithinActiveTabBaseWOSync();
  }

  /** Navigates to a tab's last in-tab route; selects the tab only after navigation succeeds. */
  async navigateToTab(tabId: string, options: TabNavigationOptions = {}): Promise<boolean> {
    const tab = this.browserTabs.getTab(tabId);
    if (tab === null) return false;

    // Select after navigate so page state does not swap while the old route is still visible.
    const pageState = this.browserTabs.getPageState(tabId);
    const { routePath } = pageState;
    const ok = await this.router.navigate(buildAgentTabCommands(tabId, routePath), {
      replaceUrl: true,
    });
    if (ok) {
      this.browserTabs.selectTab(tabId);
      const syncState = options.syncState !== false;
      if (syncState) this.agentEvents.broadcastStateSync();
    }
    return ok;
  }

  /** Opens a claimed process-event on the event view so claim does not flash through load/process-event. */
  async openClaimedProcessEvent(eventId: string): Promise<boolean> {
    if (!eventId) {
      return this.navigateWithinActiveTab();
    }

    this.browserTabs.updateActivePageState({
      pendingAction: null,
      eventProcessingMode: 'process-event',
      activeEventId: eventId,
    });
    return this.navigateWithinActiveTab(...tabProcessingRouteSegments('process-event', eventId));
  }

  /** Navigates within the active tab and persists the in-tab path on that tab. Syncs with other tabs */
  async navigateWithinActiveTab(...segments: string[]): Promise<boolean> {
    const result = await this.navigateWithinActiveTabBaseWOSync(...segments);
    this.agentEvents.broadcastStateSync();
    return result;
  }

  /** Navigates within the active tab and persists the in-tab path on that tab */
  private async navigateWithinActiveTabBaseWOSync(...segments: string[]): Promise<boolean> {
    const tabId = this.browserTabs.activeTabId();
    if (!tabId) return false;
    const routePath = segments.filter(Boolean).join('/') || TAB_EVENTS_PAGE;
    this.browserTabs.updateTabRoutePath(tabId, routePath);
    const result = await this.router.navigate(buildAgentTabCommands(tabId, routePath), {
      replaceUrl: true,
    });
    // The navigate promise is the claim UI's completion signal. Clear the landing
    // lock in this same turn so a single follow-up microtask sees the page usable.
    this.browserTabs.setClaimInteractionLocked(tabId, false);
    return result;
  }

  /** Keeps the active tab and per-tab route path aligned with the browser URL. */
  syncFromUrl(url: string): void {
    const parsed = parseAgentTabRoute(url);
    if (!parsed) {
      return;
    }

    if (!this.browserTabs.hasTab(parsed.tabId)) {
      const activeId = this.browserTabs.activeTabId();
      if (activeId) {
        void this.router.navigate(buildAgentTabCommands(activeId), { replaceUrl: true });
      }
      return;
    }

    if (this.browserTabs.activeTabId() !== parsed.tabId) {
      this.browserTabs.selectTab(parsed.tabId);
    }

    const storedPath = this.browserTabs.getPageState(parsed.tabId).routePath;
    if (storedPath !== parsed.routePath) {
      this.browserTabs.updateTabRoutePath(parsed.tabId, parsed.routePath);
    }
  }

  async closeTabWorkspace(tabId: string, options: TabNavigationOptions = {}): Promise<void> {
    const state = this.browserTabs.getPageState(tabId);
    await this.eventHandles.releaseHandleForTabState(state.eventHandleId);

    const tabs = this.browserTabs.tabs();
    const index = tabs.findIndex((tab) => tab.id === tabId);
    const fallbackId = tabs[index + 1]?.id ?? tabs[index - 1]?.id;

    this.browserTabs.removeTab(tabId);

    const nextId =
      fallbackId && this.browserTabs.hasTab(fallbackId)
        ? fallbackId
        : this.browserTabs.activeTabId();
    if (nextId) {
      await this.navigateToTab(nextId, options);
    }
  }

  async syncFromPageState(newPageState: PageSyncState, options: SyncOptions = {}) {
    const hardSync = options.hardSync === true;
    const currentTabs = this.browserTabs.tabDetails();

    if (
      hardSync &&
      this.workspaceBootstrapping &&
      newPageState.events.length === 0 &&
      options.applyEmptyDuringBoot !== true
    ) {
      return;
    }

    // Close any events not included in page state, only if hard sync.
    // The last event tab is replaced with New Event so the landing page stays open.
    if (hardSync) {
      for (const tab of currentTabs) {
        if (!tab.pageState.activeEventId) continue;
        const exists = newPageState.events.some((e) => e.id === tab.pageState.activeEventId);
        if (!exists) {
          if (this.browserTabs.tabs().length <= 1) {
            this.browserTabs.addTab();
          }
          await this.closeTabWorkspace(tab.tab.id, { syncState: false });
        }
      }
    }

    for (const event of newPageState.events) {
      const { id, eventProcessingMode, label } = event;
      const existingTab = this.browserTabs
        .tabDetails()
        .find((t) => t.pageState.activeEventId === id);
      if (existingTab) {
        const claimedAt = event.processingTimer?.segmentStartAt?.trim();
        if (hardSync && claimedAt) {
          this.browserTabs.updatePageState(existingTab.tab.id, { claimedAt });
        }
        continue;
      }

      // If active tab is 'events', open in current
      const activeTabId = this.browserTabs.activeTabId();
      if (activeTabId) {
        const tabState = this.browserTabs.getPageState(activeTabId);
        const shouldOpenInCurrent = tabState.routePath === 'events' && !tabState.activeEventId;
        if (shouldOpenInCurrent) {
          await this.setTabEvent(
            activeTabId,
            id,
            eventProcessingMode || 'process-event',
            label,
            event.siteId,
            event.processingTimer?.segmentStartAt,
          );
          continue;
        }
      }

      // Otherwise open in new tab
      const tabId = this.browserTabs.addTab();
      if (tabId) {
        await this.navigateToTab(tabId, { syncState: false });
        await this.setTabEvent(
          tabId,
          id,
          eventProcessingMode || 'process-event',
          label,
          event.siteId,
          event.processingTimer?.segmentStartAt,
        );
      }
    }
    const activeEvent =
      newPageState.activeEventId &&
      newPageState.events.find((e) => e.id === newPageState.activeEventId);
    if (activeEvent) {
      const existing = this.browserTabs.findTab(activeEvent.id);
      if (existing) await this.navigateToTab(existing.id, { syncState: false });
    }
  }

  private async setTabEvent(
    tabId: string,
    eventId: string,
    eventProcessingMode: EventProcessingMode,
    label?: string,
    siteId?: number,
    claimedAt?: string,
  ) {
    if (label) this.browserTabs.updateTabLabel(tabId, label);
    this.browserTabs.updateActivePageState({
      eventProcessingMode,
      activeEventId: eventId,
      activeEventSiteId: Number.isInteger(siteId) && (siteId ?? 0) > 0 ? siteId! : null,
      claimedAt: claimedAt?.trim() || null,
    });
    await this.navigateWithinActiveTabBaseWOSync(
      ...tabProcessingRouteSegments(eventProcessingMode, eventId),
    );
  }
}
